import { z } from "zod";

// First-party capture: the CSP in routes/__root.tsx allows only the Zoho CDNs
// — no GA, no GTM — so attribution has to be ours. localStorage rather than a
// cookie: the value never needs to reach the server on its own, it rides to
// Medusa on the cart metadata the checkout already writes, and Medusa copies
// cart.metadata -> order.metadata at completion. It has to outlive the tab: a
// shopper who opens a product in a new one, or comes back days later to buy,
// still belongs to the campaign that brought them.

const STORAGE_KEY = "lp_attribution";

// Matches the industry-standard first-touch window; past this a purchase is no
// longer credibly the campaign's.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// Exactly the parameters the Zoho CRM field map forwards
// (others/medusa/src/lib/zoho-crm/order-attribution.ts). Adding
// utm_term/utm_content here means adding the matching CRM fields there, or
// they go nowhere.
const AttributionSchema = z.object({
  utm_source: z.string().min(1).optional(),
  utm_medium: z.string().min(1).optional(),
  utm_campaign: z.string().min(1).optional(),
});

const StoredSchema = z.object({
  data: AttributionSchema,
  at: z.number().int().positive(),
});

export type Attribution = z.infer<typeof AttributionSchema>;
export const ATTRIBUTION_PARAMS = AttributionSchema.keyof().options;
export type AttributionParam = (typeof ATTRIBUTION_PARAMS)[number];

// Zoho's fields are plain text; a pathological query string should not become a
// pathological CRM record.
const MAX_VALUE_LENGTH = 200;

export function parseAttribution(search: string): Attribution {
  const params = new URLSearchParams(search);
  const found: Attribution = {};
  for (const key of ATTRIBUTION_PARAMS) {
    const value = params.get(key)?.trim();
    if (value) found[key] = value.slice(0, MAX_VALUE_LENGTH);
  }
  return found;
}

export function readAttribution(): Attribution {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const stored = StoredSchema.parse(JSON.parse(raw));
    // A clock set ahead at capture time would otherwise pin the first touch
    // permanently, locking out every later campaign on that browser.
    const age = Date.now() - stored.at;
    if (age < 0 || age > MAX_AGE_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return {};
    }
    return stored.data;
  } catch {
    // Private-mode storage or hand-edited JSON: attribution is nice to have,
    // never a reason to break a page.
    return {};
  }
}

/**
 * First touch wins. A customer who lands from a campaign, browses, then returns
 * via an organic search within the attribution window should still be credited
 * to the campaign that brought them.
 */
export function captureAttribution(search?: string): Attribution {
  if (typeof window === "undefined") return {};
  const existing = readAttribution();
  if (Object.keys(existing).length > 0) return existing;

  const found = parseAttribution(search ?? window.location.search);
  if (Object.keys(found).length === 0) return {};
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ data: found, at: Date.now() }),
    );
  } catch {
    // Storage blocked; the value is still returned to this caller.
  }
  return found;
}
